import { FC, ReactNode } from 'react';

import { ReactComponent as PaymentsIcon } from '../../icons/PaymentsIcon.svg';
import { ReactComponent as ReservationIcon } from '../../icons/ReservationIcon.svg';
import { ReactComponent as CommunityIcon } from '../../icons/CommunityIcon.svg';
import { PARTNER_LINKS, PARTNER_SECTION_IDS } from './partnerContent';

const HERO_HIGHLIGHTS = [
  { Icon: PaymentsIcon, label: 'Earn on idle hours', detail: 'Publish rates and plans, get paid via Stripe.' },
  { Icon: ReservationIcon, label: 'Bookings on your terms', detail: 'Approve requests and set your own availability.' },
  { Icon: CommunityIcon, label: 'Vetted operators', detail: 'Food businesses arrive with documents in hand.' },
];

type PartnerHeroProps = {
  /** Sign-in / sign-up panel, rendered near the top of the right-hand column. */
  children: ReactNode;
};

/**
 * Above-the-fold Partner Dashboard landing: white canvas with soft teal accents,
 * sales pitch on the left, and the auth card anchored near the top on the right.
 */
export const PartnerHero: FC<PartnerHeroProps> = ({ children }) => (
  <section className="relative overflow-hidden bg-white">
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -left-24 -top-32 h-[420px] w-[420px] rounded-full bg-primary-400/15 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[280px] w-[280px] rounded-full bg-primary-tint blur-2xl" />
    </div>

    {/* Landing-style accent rails */}
    <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 hidden w-[10px] bg-primary-400 lg:block" />
    <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 hidden w-[10px] bg-primary-400 lg:block" />

    <div className="relative mx-auto grid w-full max-w-[1450px] gap-8 px-6 pb-14 pt-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-start lg:gap-10 lg:px-12 lg:pb-16 lg:pt-8">
      <div className="partner-hero-copy max-w-[800px]">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/40 bg-primary-tint px-3 py-1 font-raleway text-[11px] font-bold uppercase tracking-[0.16em] text-primary-100">
          <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />
          For Resource Providers
        </span>

        <h2 className="mt-5 font-raleway text-[40px] font-bold leading-[1.05] tracking-tight text-ink sm:text-[48px] lg:text-[56px]">
          Monetize your kitchen{' '}
          <span className="relative inline-block text-primary-300">
            capacity
            <span aria-hidden className="absolute inset-x-0 -bottom-1 h-[7px] rounded-full bg-primary-400/35" />
          </span>{' '}
          today
        </h2>

        <p className="mt-5 max-w-[760px] font-open-sans text-[17px] leading-[1.7] text-[#3F5B64]">
          Turn unused hours, equipment, and storage into booked, paid revenue.
          Set your terms, approve requests from pre-vetted food entrepreneurs, and run the whole
          operation from one place.
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a
            href={PARTNER_LINKS.bookDemo}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-12 items-center rounded-lg bg-primaryDark-0 px-6 font-raleway text-sm font-semibold text-white transition-colors hover:bg-primary-300"
          >
            Book a demo
          </a>
          <a
            href={`#${PARTNER_SECTION_IDS.how}`}
            className="inline-flex h-12 items-center rounded-lg border border-[#D5E3E8] bg-white px-6 font-raleway text-sm font-semibold text-ink transition-colors hover:border-primary-400 hover:bg-primary-tint"
          >
            See how it works
          </a>
        </div>

        <dl className="mt-9 grid gap-3 sm:grid-cols-3">
          {HERO_HIGHLIGHTS.map(({ Icon, label, detail }) => (
            <div
              key={label}
              className="rounded-xl border border-[#E4EEF1] bg-white p-4 transition-all duration-200 hover:border-primary-400/60 hover:shadow-[0_12px_26px_rgba(1,56,71,0.08)]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-tint">
                <Icon className="h-[18px] w-[18px] text-primary-300" />
              </span>
              <dt className="mt-3 font-raleway text-[14px] font-semibold text-ink">{label}</dt>
              <dd className="mt-1 font-open-sans text-[13px] leading-relaxed text-muted">{detail}</dd>
            </div>
          ))}
        </dl>

      </div>

      {/* Auth stays near the top — sales conversion surface */}
      <div id={PARTNER_SECTION_IDS.auth} className="partner-hero-auth lg:pt-1">
        <div className="mx-auto w-full max-w-[520px] lg:ml-auto lg:mr-0 lg:sticky lg:top-[88px]">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="font-raleway text-[12px] font-semibold uppercase tracking-[0.16em] text-primary-100">
              Start earning today
            </p>
            <span className="hidden rounded-full border border-[#D5E3E8] bg-primary-tint px-2.5 py-1 font-open-sans text-[11px] text-muted sm:inline">
              No password required
            </span>
          </div>
          {children}
        </div>
      </div>
    </div>
  </section>
);

export default PartnerHero;
